import React, { ReactElement } from 'react';
import BodyText from '../Typography/BodyText/BodyText';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import Element from '../Element/Element';
import Headline from '../Typography/Headline/Headline';
import BackgroundImage from '../BackgroundImage/BackgroundImage';
import MarginSpace from '../MarginSpace/MarginSpace';
import styles from './HeroBannerLayout.module.scss';
import { CreateProps } from '../../types/utils/CreateProps';
import { SupportedHeadlineColor } from '../Typography/Headline/Headline';
import { SupportedTagName } from '../Typography/Typography';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { SupportedColor as BackgroundSupportedColor } from '../Element/Element';

type HeroBannerLayoutProps = CreateProps<
  {
    /** Breadcrumb component with all required props */
    breadcrumb?: typeof Breadcrumb | ReactElement;
    /** Component or Text of heading */
    heading: ReactElement | string;
    /** Component or Text of description */
    description?: ReactElement | string;
    /** Image or component (of the product) to be shown on the right side of the banner */
    image?: ReactElement | string;
    /**  Image alt text, fallbacks src */
    imageAlt?: string;
    /** Background image */
    backgroundImageUrl?: string;
    /** Background color */
    backgroundColor?: BackgroundSupportedColor;
    /** Color of heading and description text */
    color?: SupportedHeadlineColor;
    /** TagName of Headline */
    headlineTagName?: SupportedTagName;
    /** Component or text of bottom component */
    bottomContent?: ReactElement | string;
    /** HTML tag name: div, span, h1 etc */
    tagName?: keyof JSX.IntrinsicElements;
  },
  typeof Element,
  'occupyVerticalSpace' | 'tagName'
>;
// Alias for backwards compatibility
export type HeroBannerLayoutAttributes = HeroBannerLayoutProps;

const HeroBannerLayout: React.FunctionComponent<HeroBannerLayoutProps> = ({
  breadcrumb,
  heading,
  description,
  image,
  imageAlt = '',
  backgroundImageUrl,
  color,
  bottomContent,
  className,
  tagName = 'div',
  headlineTagName,
  ...props
}) => {
  const resolveBackgroundImage = backgroundImageUrl
    ? {
        backgroundImage: `url(${backgroundImageUrl})`,
      }
    : undefined;
  return (
    <Element
      occupyVerticalSpace
      tagName={tagName}
      style={resolveBackgroundImage}
      className={[styles.heroBanner, className]}
      {...props}
    >
      <MarginSpace>
        <Flex
          centeredVertically
          padding={{ vertical: 'lg' }}
          className={[
            styles.heroBanner_contentContainer,
            !description &&
              styles.heroBanner_contentContainer__noDescriptionText,
          ]}
        >
          <Div flexItem>
            <Div responsiveHidden={{ thinnerThan: 'sm' }}>{breadcrumb}</Div>

            <Gutter size="xs" responsiveHidden={{ thinnerThan: 'sm' }} />

            {React.isValidElement(heading) ? (
              heading
            ) : (
              <Headline
                tagName={headlineTagName}
                size="lg"
                color={color || ('black' as SupportedHeadlineColor)}
                margin={{ bottom: 'lg' }}
                block
              >
                {heading}
              </Headline>
            )}

            {image && (
              <Div
                alignTextToCenter
                responsiveHidden={{ widerThan: 'xs' }}
                margin={{ bottom: 'xlg' }}
              >
                {React.isValidElement(image) ? (
                  image
                ) : (
                  <BackgroundImage
                    inlineBlock
                    scaleWithOriginalAspectRatio
                    className={styles.heroBanner_image}
                    src={image}
                    alt={imageAlt}
                  />
                )}
              </Div>
            )}

            {description && (
              <Div className={styles.heroBanner_descriptionContainer}>
                {React.isValidElement(description) ? (
                  description
                ) : (
                  <BodyText tagName="span" color={color || 'gray500'} size="sm">
                    {description}
                  </BodyText>
                )}
              </Div>
            )}

            {bottomContent && (
              <React.Fragment>
                <Gutter size="xlg" />

                <Div className={styles.heroBanner_bottomContent}>
                  {React.isValidElement(bottomContent) ? (
                    bottomContent
                  ) : (
                    <BodyText
                      tagName="span"
                      color={color || 'gray500'}
                      size="sm"
                    >
                      {bottomContent}
                    </BodyText>
                  )}
                </Div>
              </React.Fragment>
            )}
          </Div>

          {image && (
            <Div
              flexItem
              alignTextToCenter
              responsiveHidden={{ thinnerThan: 'sm' }}
            >
              {React.isValidElement(image) ? (
                image
              ) : (
                <BackgroundImage
                  scaleWithOriginalAspectRatio
                  inlineBlock
                  className={styles.heroBanner_image}
                  src={image}
                  alt={imageAlt}
                />
              )}
            </Div>
          )}
        </Flex>
      </MarginSpace>
    </Element>
  );
};

export default HeroBannerLayout;
